import {
  type AITrace,
  type AITraceEntry,
  type AITraceSection,
} from "@inrange/building-manager-api-client";
import { useState } from "react";

// Reusable viewer for a complete AI interaction trace (see building-manager-api
// docs/design-ai-trace-capture.md). Renders the summary header + every step in wire
// order - system prompt, tool catalogue, user prompt, thinking, text, tool calls/results -
// with per-entry collapse and recursive nested-section collapse so a 2000-line file read
// or the full system prompt stays manageable. Used by the demand-coach modals and the
// portfolio taxonomy trace. Content is shown as preformatted text (no markdown dep).

const TYPE_META: Record<string, { label: string; color: string; collapsed: boolean }> = {
  system_prompt: { label: "System prompt", color: "#6d28d9", collapsed: true },
  tool_catalogue: { label: "Tools available", color: "#6d28d9", collapsed: true },
  user_prompt: { label: "User prompt", color: "#2563eb", collapsed: true },
  thinking: { label: "Thinking", color: "#b45309", collapsed: true },
  text: { label: "Assistant", color: "#111827", collapsed: false },
  tool_use: { label: "Tool call", color: "#0369a1", collapsed: false },
  tool_result: { label: "Tool result", color: "#0f766e", collapsed: true },
};

const monospace: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: "11px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const Disclosure = ({
  summary,
  color,
  defaultOpen,
  children,
}: {
  summary: React.ReactNode;
  color?: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: color ?? "#334155",
          fontWeight: 600,
          fontSize: "11px",
          textAlign: "left",
        }}
      >
        {open ? "▾" : "▸"} {summary}
      </button>
      {open && <div style={{ marginLeft: 12, marginTop: 4 }}>{children}</div>}
    </div>
  );
};

const lineCountLabel = (section: AITraceSection): string => {
  const n = section.lineCount ?? (section.content ? section.content.split("\n").length : 0);
  return n ? ` (${n.toLocaleString()} line${n === 1 ? "" : "s"})` : "";
};

const SectionView = ({ section }: { section: AITraceSection }) => {
  const hasChildren = !!section.sections && section.sections.length > 0;
  const summary = (
    <>
      {section.label}
      {lineCountLabel(section)}
      {section.truncated ? <span style={{ color: "#b45309" }}> ⚠ trimmed</span> : null}
    </>
  );
  return (
    <Disclosure summary={summary} defaultOpen={!section.defaultCollapsed && !hasChildren}>
      {hasChildren ? (
        section.sections!.map((child, i) => <SectionView key={i} section={child} />)
      ) : (
        <div style={monospace}>{section.content}</div>
      )}
    </Disclosure>
  );
};

const timing = (e: AITraceEntry): string => {
  const parts: string[] = [];
  if (typeof e.modelSeconds === "number") parts.push(`model ${e.modelSeconds}s`);
  if (typeof e.toolSeconds === "number") parts.push(`tool ${e.toolSeconds}s`);
  if (typeof e.atSeconds === "number") parts.push(`@${e.atSeconds}s`);
  return parts.length ? ` · ${parts.join(" · ")}` : "";
};

const EntryView = ({ entry }: { entry: AITraceEntry }) => {
  const meta = TYPE_META[entry.type ?? ""] ?? {
    label: entry.type ?? "step",
    color: "#334155",
    collapsed: false,
  };
  const heading = `${entry.label ?? meta.label}${entry.name ? `: ${entry.name}` : ""}${timing(entry)}`;
  const hasSections = !!entry.sections && entry.sections.length > 0;

  // tool_use: render the input JSON; otherwise the content (or nested sections).
  const body = hasSections ? (
    entry.sections!.map((s, i) => <SectionView key={i} section={s} />)
  ) : entry.type === "tool_use" ? (
    <div style={monospace}>{JSON.stringify(entry.input ?? {}, null, 2)}</div>
  ) : (
    <div style={{ ...monospace, color: entry.isError ? "#b91c1c" : undefined }}>
      {entry.content}
    </div>
  );

  return (
    <Disclosure
      summary={<span style={{ color: meta.color }}>{heading}</span>}
      color={meta.color}
      defaultOpen={!meta.collapsed}
    >
      {body}
    </Disclosure>
  );
};

const usageSummary = (trace: AITrace): string => {
  const u = trace.usage ?? {};
  const bits: string[] = [];
  if (typeof trace.totalSeconds === "number") bits.push(`${trace.totalSeconds}s`);
  bits.push(`${trace.entries.length} steps`);
  if (u.input_tokens || u.output_tokens) {
    bits.push(`${u.input_tokens ?? 0} in / ${u.output_tokens ?? 0} out tokens`);
  }
  if (u.cache_read_input_tokens) bits.push(`${u.cache_read_input_tokens} cache-read`);
  if (trace.model_id) bits.push(trace.model_id);
  if (trace.stop_reason) bits.push(`stop: ${trace.stop_reason}`);
  return bits.join(" · ");
};

const AITraceViewer = ({ trace }: { trace: AITrace }) => {
  if (!trace || !trace.entries || trace.entries.length === 0) {
    return <div style={{ ...monospace, color: "#64748b" }}>No trace available.</div>;
  }
  return (
    <div
      style={{
        background: "rgba(0,0,0,0.04)",
        borderRadius: 4,
        padding: "8px",
        maxHeight: "50vh",
        overflowY: "auto",
      }}
    >
      <div style={{ color: "#475569", fontSize: "11px", marginBottom: 8 }}>
        {usageSummary(trace)}
        {trace.truncatedForDisplay ? (
          <span style={{ color: "#b45309" }}> · ⚠ some content trimmed for display</span>
        ) : null}
      </div>
      {trace.entries.map((entry, i) => (
        <EntryView key={i} entry={entry} />
      ))}
    </div>
  );
};

export default AITraceViewer;
